import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Combobox } from '@/components/ui/combobox';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { MultiSelect } from '@/components/ui/multi-select';
import { TestConfiguration, Subject, DifficultyDistribution } from '@/types';
import { testConfigurationApi, questionApi } from '@/lib/api';
import api from '@/lib/api';
import { KitCombobox } from './KitCombobox';
import { FileText, Plus, Search, Settings, X } from 'lucide-react';
import { getErrorInfo } from '@/lib/errorUtils';

interface EnhancedTestCreateFormProps {
  subjects: Subject[];
  onSubmit: (testData: any) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface QuestionSearchResult {
  id: string;
  questionText: string;
  subject?: string;
  topic?: string;
  difficultyLevel?: string;
  tags?: string[];
}

const TEST_TITLE_MAX_LENGTH = 120;

export const EnhancedTestCreateForm: React.FC<EnhancedTestCreateFormProps> = ({
  subjects,
  onSubmit,
  onCancel,
  loading: externalLoading = false
}) => {
  const [testType, setTestType] = useState<'standard' | 'custom'>('standard');
  const [examTypes, setExamTypes] = useState<string[]>([]);
  const [selectedExamType, setSelectedExamType] = useState<string>('');
  const [standardConfigurations, setStandardConfigurations] = useState<TestConfiguration[]>([]);
  const [selectedConfiguration, setSelectedConfiguration] = useState<TestConfiguration | null>(null);
  const [loading] = useState(false);
  const [loadingExamTypes, setLoadingExamTypes] = useState(false);
  const [loadingConfigurations, setLoadingConfigurations] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [topics, setTopics] = useState<string[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagSearchValue, setTagSearchValue] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  const [questionSearchResults, setQuestionSearchResults] = useState<QuestionSearchResult[]>([]);
  const [searchingQuestions, setSearchingQuestions] = useState(false);
  const [selectedQuestionDetails, setSelectedQuestionDetails] = useState<Record<string, QuestionSearchResult>>({});

  // Standard test form data
  const [standardFormData, setStandardFormData] = useState({
    title: '',
    description: '',
    configurationId: ''
  });

  // Custom test form data
  const [customFormData, setCustomFormData] = useState({
    title: '',
    description: '',
    subjectIds: [] as string[],
    subjectNames: [] as string[],
    topicIds: [] as string[],
    topicNames: [] as string[],
    totalQuestions: 30,
    duration: 60,
    kitNumbers: [] as string[],
    difficultyDistribution: {
      easyPercentage: 40,
      mediumPercentage: 40,
      hardPercentage: 20
    } as DifficultyDistribution,
    difficultyDistributionEnabled: true,
    markingScheme: {
      correctMarks: 4,
      incorrectMarks: -1,
      unansweredMarks: 0,
      negativeMarkingEnabled: true,
      maxMarks: 0 // Will be calculated as totalQuestions * correctMarks
    },
    tagFilters: [] as string[],
    selectedQuestionIds: [] as string[],
    autoGenerateRemainingQuestions: true
  });



  // Load exam types on component mount
  useEffect(() => {
    if (testType === 'standard') {
      loadExamTypes();
    }
  }, [testType]);

  // Load configurations when exam type is selected
  useEffect(() => {
    if (testType === 'standard' && selectedExamType) {
      loadConfigurationsByExamType(selectedExamType);
    }
  }, [testType, selectedExamType]);

  useEffect(() => {
    if (testType === 'custom') {
      searchTags();
    }
  }, [testType]);

  // Load available exam types
  const loadExamTypes = async () => {
    try {
      setLoadingExamTypes(true);
      console.log('Loading available exam types...');
      const examTypesResponse = await testConfigurationApi.getExamTypes();
      console.log('Exam types response:', examTypesResponse);

      setExamTypes(examTypesResponse || []);
    } catch (error) {
      console.error('Failed to load exam types:', error);
      setExamTypes([]);
      const errorInfo = getErrorInfo(error);
      setErrors({
        general: `Failed to load exam types: ${errorInfo.message}`
      });
    } finally {
      setLoadingExamTypes(false);
    }
  };

  // Load configurations by exam type
  const loadConfigurationsByExamType = async (examType: string) => {
    try {
      setLoadingConfigurations(true);
      console.log('Loading configurations for exam type:', examType);
      const configurationsResponse = await testConfigurationApi.getByExamType(examType);
      console.log('Configurations response:', configurationsResponse);

      setStandardConfigurations(configurationsResponse || []);
    } catch (error) {
      console.error('Failed to load configurations for exam type:', error);
      setStandardConfigurations([]);
      const errorInfo = getErrorInfo(error);
      setErrors({
        general: `Failed to load test patterns: ${errorInfo.message}`
      });
    } finally {
      setLoadingConfigurations(false);
    }
  };

  // Handle configuration selection
  const handleConfigurationChange = (configId: string) => {
    const config = standardConfigurations.find(c => c.id === configId);
    setSelectedConfiguration(config || null);
    setStandardFormData(prev => ({
      ...prev,
      configurationId: configId
    }));
  };

  // Load topics when subjects change
  const loadTopics = async (subjectNames: string[]) => {
    if (!subjectNames || subjectNames.length === 0) {
      setTopics([]);
      return;
    }

    try {
      setLoadingTopics(true);
      const allTopics = new Set<string>();

      // Load topics for each selected subject
      for (const subjectName of subjectNames) {
        try {
          const response = await api.get(`/admin/subjects/${encodeURIComponent(subjectName)}/topics`);
          const subjectTopics = response.data || [];
          subjectTopics.forEach((topic: string) => allTopics.add(topic));
        } catch (error) {
          console.error(`Failed to load topics for subject ${subjectName}:`, error);
        }
      }

      setTopics(Array.from(allTopics).sort());
    } catch (error) {
      console.error('Failed to load topics:', error);
      setTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  };

  // Handle subject change for custom tests (multi-select)
  const handleSubjectChange = (selectedSubjectIds: string[]) => {
    const selectedSubjects = subjects.filter(s => selectedSubjectIds.includes(s.id));
    const subjectNames = selectedSubjects.map(s => s.name);

    setCustomFormData(prev => ({
      ...prev,
      subjectIds: selectedSubjectIds,
      subjectNames: subjectNames,
      topicIds: [], // Reset topics when subjects change
      topicNames: []
    }));

    // Load topics for the selected subjects
    if (subjectNames.length > 0) {
      loadTopics(subjectNames);
    } else {
      setTopics([]);
    }
  };

  // Handle topic change for custom tests (multi-select)
  const handleTopicChange = (selectedTopicNames: string[]) => {
    setCustomFormData(prev => ({
      ...prev,
      topicIds: selectedTopicNames, // Using topic names as IDs for simplicity
      topicNames: selectedTopicNames
    }));
  };

  // Calculate suggested duration based on number of questions
  const calculateSuggestedDuration = (totalQuestions: number): number => {
    // Base formula: 2.5 minutes per question for MCQ, with minimum of 15 minutes
    const baseTimePerQuestion = 2.5;
    const calculatedDuration = Math.ceil(totalQuestions * baseTimePerQuestion);
    return Math.max(calculatedDuration, 15); // Minimum 15 minutes
  };

  // Handle total questions change with automatic duration calculation
  const handleTotalQuestionsChange = (totalQuestions: number) => {
    const suggestedDuration = calculateSuggestedDuration(totalQuestions);

    setCustomFormData(prev => ({
      ...prev,
      totalQuestions,
      duration: suggestedDuration // Auto-update duration
    }));
  };

  const searchTags = async (search: string = '') => {
    try {
      setLoadingTags(true);
      const response = await questionApi.getAll({
        page: 0,
        size: 100,
        search: search.trim() || undefined
      });

      const content = response?.content || response?.data?.content || response?.data || [];
      const uniqueTags = Array.from(
        new Set(
          (Array.isArray(content) ? content : [])
            .flatMap((question: QuestionSearchResult) => question.tags || [])
            .map(tag => tag.trim())
            .filter(tag => !!tag)
        )
      ).sort((a, b) => a.localeCompare(b));

      setAvailableTags(uniqueTags);
    } catch (error) {
      console.error('Failed to search tags:', error);
      setAvailableTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  const debouncedTagSearch = useCallback(
    debounce((search: string) => {
      searchTags(search);
    }, 300),
    []
  );

  const handleTagSearch = (search: string) => {
    setTagSearchValue(search);
    debouncedTagSearch(search);
  };

  const addTagFilter = (tagValue: string) => {
    const normalizedTag = tagValue.trim().toLowerCase();
    if (!normalizedTag) return;

    if (!customFormData.tagFilters.includes(normalizedTag)) {
      setCustomFormData(prev => ({
        ...prev,
        tagFilters: [...prev.tagFilters, normalizedTag]
      }));
    }
    setTagSearchValue('');
    setSelectedTag('');
  };

  const removeTagFilter = (tagToRemove: string) => {
    setCustomFormData(prev => ({
      ...prev,
      tagFilters: prev.tagFilters.filter(tag => tag !== tagToRemove)
    }));
  };

  const searchQuestionsForCherryPick = async () => {
    try {
      setSearchingQuestions(true);

      const response = await questionApi.getAll({
        page: 0,
        size: 50,
        search: questionSearchTerm.trim() || undefined,
        tags: customFormData.tagFilters.length > 0 ? customFormData.tagFilters.join(',') : undefined
      });

      const content = response?.content || response?.data?.content || response?.data || [];
      const results = Array.isArray(content) ? content : [];
      setQuestionSearchResults(results);
      setSelectedQuestionDetails(prev => {
        const updated = { ...prev };
        results.forEach((question) => {
          if (customFormData.selectedQuestionIds.includes(question.id)) {
            updated[question.id] = question;
          }
        });
        return updated;
      });
    } catch (error) {
      console.error('Failed to search questions:', error);
      setQuestionSearchResults([]);
      setErrors(prev => ({
        ...prev,
        questionSearch: 'Failed to load questions for selection'
      }));
    } finally {
      setSearchingQuestions(false);
    }
  };

  const toggleCherryPickedQuestion = (questionId: string) => {
    setCustomFormData(prev => {
      const alreadySelected = prev.selectedQuestionIds.includes(questionId);
      const resultQuestion = questionSearchResults.find(q => q.id === questionId);

      setSelectedQuestionDetails(detailsPrev => {
        if (alreadySelected) {
          const { [questionId]: _removed, ...rest } = detailsPrev;
          return rest;
        }
        if (resultQuestion) {
          return { ...detailsPrev, [questionId]: resultQuestion };
        }
        return detailsPrev;
      });

      return {
        ...prev,
        selectedQuestionIds: alreadySelected
          ? prev.selectedQuestionIds.filter(id => id !== questionId)
          : [...prev.selectedQuestionIds, questionId]
      };
    });
  };

  const selectedQuestionsPreview = customFormData.selectedQuestionIds.map((id) => (
    selectedQuestionDetails[id] || questionSearchResults.find((question) => question.id === id) || {
      id,
      questionText: `Question ID: ${id}`
    }
  ));
  const difficultyTotal = customFormData.difficultyDistribution.easyPercentage +
    customFormData.difficultyDistribution.mediumPercentage +
    customFormData.difficultyDistribution.hardPercentage;



  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (testType === 'standard') {
      if (!standardFormData.title.trim()) {
        newErrors.title = 'Test title is required';
      } else if (standardFormData.title.trim().length > TEST_TITLE_MAX_LENGTH) {
        newErrors.title = `Test title must be ${TEST_TITLE_MAX_LENGTH} characters or fewer`;
      }
      if (!selectedExamType) {
        newErrors.examType = 'Exam type is required';
      }
      if (!standardFormData.configurationId) {
        newErrors.configuration = 'Test configuration is required';
      }
    } else {
      if (!customFormData.title.trim()) {
        newErrors.title = 'Test title is required';
      } else if (customFormData.title.trim().length > TEST_TITLE_MAX_LENGTH) {
        newErrors.title = `Test title must be ${TEST_TITLE_MAX_LENGTH} characters or fewer`;
      }
      if (!customFormData.subjectIds || customFormData.subjectIds.length === 0) {
        newErrors.subjects = 'At least one subject must be selected';
      }
      if (customFormData.totalQuestions < 1 || customFormData.totalQuestions > 200) {
        newErrors.totalQuestions = 'Total questions must be between 1 and 200';
      }
      if (customFormData.duration < 5 || customFormData.duration > 300) {
        newErrors.duration = 'Duration must be between 5 and 300 minutes';
      }
      if (customFormData.selectedQuestionIds.length > customFormData.totalQuestions) {
        newErrors.selectedQuestionIds = 'Cherry-picked questions cannot exceed total question count';
      }
      // Kit numbers are optional - no validation needed
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (testType === 'standard') {
      onSubmit({
        type: 'standard',
        configurationId: standardFormData.configurationId,
        title: standardFormData.title,
        description: standardFormData.description
      });
    } else {
      onSubmit({
        type: 'custom',
        ...customFormData
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {errors.general}
        </div>
      )}

      {/* Test Type Selection */}
      <div className="space-y-2">
        <Label>Test Type</Label>
        <Tabs value={testType} onValueChange={(value) => setTestType(value as 'standard' | 'custom')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="standard" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Standard Tests
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Custom Tests
            </TabsTrigger>
          </TabsList>

          <TabsContent value="standard" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Standard Test Configuration</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Choose from predefined exam patterns with automatic question selection
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="standard-title">Test Title</Label>
                  <Input
                    id="standard-title"
                    value={standardFormData.title}
                    onChange={(e) => setStandardFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter test title"
                    maxLength={TEST_TITLE_MAX_LENGTH}
                    className={errors.title ? 'border-red-500' : ''}
                  />
                  <p className="text-xs text-muted-foreground">
                    Use a student-friendly name, max {TEST_TITLE_MAX_LENGTH} characters ({standardFormData.title.length}/{TEST_TITLE_MAX_LENGTH}).
                  </p>
                  {errors.title && <p className="text-sm text-red-600">{errors.title}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="standard-description">Description (Optional)</Label>
                  <Textarea
                    id="standard-description"
                    value={standardFormData.description}
                    onChange={(e) => setStandardFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter test description"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="exam-type">Exam Type</Label>
                  <Select value={selectedExamType} onValueChange={setSelectedExamType}>
                    <SelectTrigger className={errors.examType ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select exam type" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingExamTypes ? (
                        <SelectItem value="loading" disabled>Loading exam types...</SelectItem>
                      ) : examTypes.length === 0 ? (
                        <SelectItem value="empty" disabled>No exam types available</SelectItem>
                      ) : (
                        examTypes.map((examType) => (
                          <SelectItem key={examType} value={examType}>
                            {examType}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {errors.examType && <p className="text-sm text-red-600">{errors.examType}</p>}
                </div>

                {selectedExamType && (
                  <div className="space-y-2">
                    <Label htmlFor="configuration">Exam Pattern</Label>
                    <Select value={standardFormData.configurationId} onValueChange={handleConfigurationChange}>
                      <SelectTrigger className={errors.configuration ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select exam pattern" />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingConfigurations ? (
                          <SelectItem value="loading" disabled>Loading patterns...</SelectItem>
                        ) : standardConfigurations.length === 0 ? (
                          <SelectItem value="empty" disabled>No patterns available for this exam type</SelectItem>
                        ) : (
                          standardConfigurations.map((config) => (
                            <SelectItem key={config.id} value={config.id}>
                              {config.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {errors.configuration && <p className="text-sm text-red-600">{errors.configuration}</p>}
                  </div>
                )}

                {selectedConfiguration && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4">
                      <div className="space-y-2">
                        <h4 className="font-medium text-blue-900">{selectedConfiguration.name}</h4>
                        <p className="text-sm text-blue-700">{selectedConfiguration.description}</p>
                        <div className="flex gap-4 text-sm text-blue-600">
                          <span>Duration: {selectedConfiguration.totalDuration} minutes</span>
                          <span>Questions: {selectedConfiguration.totalQuestions}</span>
                          <span>Type: {selectedConfiguration.examType}</span>
                        </div>
                        {selectedConfiguration.subjectConfigurations && (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-blue-800">Subjects:</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedConfiguration.subjectConfigurations.map((subject, index) => (
                                <Badge key={index} variant="outline" className="text-blue-700 border-blue-300">
                                  {subject.subjectName} ({subject.questionCount}Q)
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Custom Test Configuration</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Create a test with your own parameters and question selection criteria
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="custom-title">Test Title</Label>
                    <Input
                      id="custom-title"
                      value={customFormData.title}
                      onChange={(e) => setCustomFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter test title"
                      maxLength={TEST_TITLE_MAX_LENGTH}
                      className={errors.title ? 'border-red-500' : ''}
                    />
                    <p className="text-xs text-muted-foreground">
                      Use a student-friendly name, max {TEST_TITLE_MAX_LENGTH} characters ({customFormData.title.length}/{TEST_TITLE_MAX_LENGTH}).
                    </p>
                    {errors.title && <p className="text-sm text-red-600">{errors.title}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subjects">Subjects</Label>
                    <MultiSelect
                      options={subjects.map(subject => ({ label: subject.name, value: subject.id }))}
                      selected={customFormData.subjectIds}
                      onChange={handleSubjectChange}
                      placeholder="Select subjects"
                      className={errors.subjects ? 'border-red-500' : ''}
                    />
                    {errors.subjects && <p className="text-sm text-red-600">{errors.subjects}</p>}
                    <p className="text-xs text-muted-foreground">
                      Select one or more subjects for the test
                    </p>
                  </div>
                </div>

                {/* Topic Selection - Only show if subjects are selected */}
                {customFormData.subjectIds.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="topics">Topics (Optional)</Label>
                    <MultiSelect
                      options={topics.map(topic => ({ label: topic, value: topic }))}
                      selected={customFormData.topicNames}
                      onChange={handleTopicChange}
                      placeholder={loadingTopics ? "Loading topics..." : "Select topics (optional)"}
                      disabled={loadingTopics}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty to include questions from all topics in the selected subjects
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="custom-description">Description (Optional)</Label>
                  <Textarea
                    id="custom-description"
                    value={customFormData.description}
                    onChange={(e) => setCustomFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter test description"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="totalQuestions">Total Questions</Label>
                    <Input
                      id="totalQuestions"
                      type="number"
                      min="1"
                      max="200"
                      value={customFormData.totalQuestions}
                      onChange={(e) => handleTotalQuestionsChange(parseInt(e.target.value) || 0)}
                      className={errors.totalQuestions ? 'border-red-500' : ''}
                    />
                    {errors.totalQuestions && <p className="text-sm text-red-600">{errors.totalQuestions}</p>}
                    <p className="text-xs text-muted-foreground">
                      Suggested duration: {calculateSuggestedDuration(customFormData.totalQuestions)} minutes
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration">Duration (minutes)</Label>
                    <Input
                      id="duration"
                      type="number"
                      min="5"
                      max="300"
                      value={customFormData.duration}
                      onChange={(e) => setCustomFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                      className={errors.duration ? 'border-red-500' : ''}
                    />
                    {errors.duration && <p className="text-sm text-red-600">{errors.duration}</p>}
                    <p className="text-xs text-muted-foreground">
                      Auto-calculated based on question count. You can override this value.
                    </p>
                  </div>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Question Pool Filters</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Refine where questions can be selected from using kits and tags.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <KitCombobox
                        selectedKits={customFormData.kitNumbers}
                        onKitsChange={(kits) => setCustomFormData(prev => ({ ...prev, kitNumbers: kits }))}
                        error={errors.kitNumbers}
                      />
                    </div>

                    <div className="space-y-3">
                      <Label>Question Tags (Optional)</Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Combobox
                            options={availableTags}
                            value={selectedTag}
                            onValueChange={setSelectedTag}
                            placeholder="Search and select tag..."
                            searchPlaceholder="Type to search tags..."
                            emptyMessage="No tags found. Type to add a new tag."
                            loading={loadingTags}
                            onSearch={handleTagSearch}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => addTagFilter(selectedTag)}
                          disabled={!selectedTag}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={tagSearchValue}
                          onChange={(e) => setTagSearchValue(e.target.value)}
                          placeholder="Or type a custom tag..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addTagFilter(tagSearchValue);
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => addTagFilter(tagSearchValue)}
                          disabled={!tagSearchValue.trim()}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 border rounded-md p-2 min-h-[44px]">
                        {customFormData.tagFilters.length === 0 ? (
                          <span className="text-sm text-muted-foreground">No tag filters selected</span>
                        ) : (
                          customFormData.tagFilters.map((tag) => (
                            <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeTagFilter(tag)}
                                className="ml-1"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Optional: pick tags from dropdown using typeahead or add custom tags. Leave empty to include all tags.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Cherry-pick questions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Cherry-pick Questions (Optional)</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Manually add specific questions, then fill remaining slots automatically.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={questionSearchTerm}
                        onChange={(e) => setQuestionSearchTerm(e.target.value)}
                        placeholder="Search by question text, subject, topic, or tag..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            searchQuestionsForCherryPick();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={searchQuestionsForCherryPick}
                        disabled={searchingQuestions}
                      >
                        <Search className="h-4 w-4 mr-1" />
                        {searchingQuestions ? 'Searching...' : 'Search'}
                      </Button>
                    </div>

                    {errors.questionSearch && <p className="text-sm text-red-600">{errors.questionSearch}</p>}
                    {errors.selectedQuestionIds && <p className="text-sm text-red-600">{errors.selectedQuestionIds}</p>}

                    <div className="flex items-center gap-3 text-sm">
                      <span className="font-medium">Selected:</span>
                      <Badge variant="outline">{customFormData.selectedQuestionIds.length}</Badge>
                      <span className="text-muted-foreground">of {customFormData.totalQuestions} total</span>
                    </div>

                    <div className="space-y-2">
                      <Label>Cherry-picked Preview</Label>
                      <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-2">
                        {selectedQuestionsPreview.length === 0 ? (
                          <p className="text-sm text-muted-foreground px-2 py-3">
                            No questions selected yet.
                          </p>
                        ) : (
                          selectedQuestionsPreview.map((question) => (
                            <div key={`selected-${question.id}`} className="flex items-start justify-between gap-3 p-2 rounded border bg-blue-50/40">
                              <div className="min-w-0">
                                <p className="text-sm font-medium line-clamp-2">{question.questionText || 'Untitled question'}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {question.subject && <Badge variant="outline">{question.subject}</Badge>}
                                  {question.topic && <Badge variant="outline">{question.topic}</Badge>}
                                  {question.difficultyLevel && <Badge variant="outline">{question.difficultyLevel}</Badge>}
                                  {(question.tags || []).slice(0, 3).map(tag => (
                                    <Badge key={`selected-${question.id}-${tag}`} variant="secondary">{tag}</Badge>
                                  ))}
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleCherryPickedQuestion(question.id)}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto space-y-2 border rounded-md p-2">
                      {questionSearchResults.length === 0 ? (
                        <p className="text-sm text-muted-foreground px-2 py-3">
                          Search questions to cherry-pick from the pool.
                        </p>
                      ) : (
                        questionSearchResults.map((question) => {
                          const checked = customFormData.selectedQuestionIds.includes(question.id);
                          return (
                            <label
                              key={question.id}
                              className={`flex items-start gap-3 p-2 rounded border cursor-pointer ${
                                checked ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleCherryPickedQuestion(question.id)}
                                className="mt-1"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium line-clamp-2">{question.questionText || 'Untitled question'}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {question.subject && <Badge variant="outline">{question.subject}</Badge>}
                                  {question.topic && <Badge variant="outline">{question.topic}</Badge>}
                                  {question.difficultyLevel && <Badge variant="outline">{question.difficultyLevel}</Badge>}
                                  {(question.tags || []).slice(0, 3).map(tag => (
                                    <Badge key={`${question.id}-${tag}`} variant="secondary">{tag}</Badge>
                                  ))}
                                </div>
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        id="autoGenerateRemainingQuestions"
                        type="checkbox"
                        checked={customFormData.autoGenerateRemainingQuestions}
                        onChange={(e) => setCustomFormData(prev => ({
                          ...prev,
                          autoGenerateRemainingQuestions: e.target.checked
                        }))}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="autoGenerateRemainingQuestions" className="cursor-pointer">
                        Auto-fill remaining questions using on-demand logic
                      </Label>
                    </div>
                  </CardContent>
                </Card>

                {/* Marking Scheme */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Marking Scheme</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Configure how marks are awarded for correct, incorrect, and unanswered questions
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="correctMarks">Marks for Correct Answer</Label>
                        <Input
                          id="correctMarks"
                          type="number"
                          min="0"
                          step="0.5"
                          value={customFormData.markingScheme.correctMarks}
                          onChange={(e) => setCustomFormData(prev => ({
                            ...prev,
                            markingScheme: {
                              ...prev.markingScheme,
                              correctMarks: parseFloat(e.target.value) || 0
                            }
                          }))}
                        />
                        <p className="text-xs text-muted-foreground">Points awarded per correct answer</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="incorrectMarks">Marks for Incorrect Answer</Label>
                        <Input
                          id="incorrectMarks"
                          type="number"
                          step="0.5"
                          value={customFormData.markingScheme.incorrectMarks}
                          onChange={(e) => setCustomFormData(prev => ({
                            ...prev,
                            markingScheme: {
                              ...prev.markingScheme,
                              incorrectMarks: parseFloat(e.target.value) || 0
                            }
                          }))}
                        />
                        <p className="text-xs text-muted-foreground">Points deducted per wrong answer (use negative values)</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="unansweredMarks">Marks for Unanswered</Label>
                        <Input
                          id="unansweredMarks"
                          type="number"
                          step="0.5"
                          value={customFormData.markingScheme.unansweredMarks}
                          onChange={(e) => setCustomFormData(prev => ({
                            ...prev,
                            markingScheme: {
                              ...prev.markingScheme,
                              unansweredMarks: parseFloat(e.target.value) || 0
                            }
                          }))}
                        />
                        <p className="text-xs text-muted-foreground">Points for unanswered questions (usually 0)</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="negativeMarking"
                        checked={customFormData.markingScheme.negativeMarkingEnabled}
                        onChange={(e) => setCustomFormData(prev => ({
                          ...prev,
                          markingScheme: {
                            ...prev.markingScheme,
                            negativeMarkingEnabled: e.target.checked,
                            incorrectMarks: e.target.checked ? prev.markingScheme.incorrectMarks : 0
                          }
                        }))}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="negativeMarking" className="cursor-pointer">
                        Enable Negative Marking
                      </Label>
                    </div>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>Maximum Marks:</strong> {customFormData.totalQuestions * customFormData.markingScheme.correctMarks} 
                        ({customFormData.totalQuestions} questions × {customFormData.markingScheme.correctMarks} marks)
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Difficulty Distribution</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Define how many easy, medium, and hard questions should be picked.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        id="difficultyDistributionEnabled"
                        type="checkbox"
                        checked={customFormData.difficultyDistributionEnabled}
                        onChange={(e) => setCustomFormData(prev => ({
                          ...prev,
                          difficultyDistributionEnabled: e.target.checked
                        }))}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="difficultyDistributionEnabled" className="cursor-pointer">
                        Enable difficulty distribution
                      </Label>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Easy Questions</span>
                        <span className="text-sm text-muted-foreground">{customFormData.difficultyDistribution.easyPercentage}%</span>
                      </div>
                      <Slider
                        value={[customFormData.difficultyDistribution.easyPercentage]}
                        onValueChange={([value]) => setCustomFormData(prev => ({
                          ...prev,
                          difficultyDistribution: { ...prev.difficultyDistribution, easyPercentage: value }
                        }))}
                        max={100}
                        step={5}
                        className="w-full"
                        disabled={!customFormData.difficultyDistributionEnabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Medium Questions</span>
                        <span className="text-sm text-muted-foreground">{customFormData.difficultyDistribution.mediumPercentage}%</span>
                      </div>
                      <Slider
                        value={[customFormData.difficultyDistribution.mediumPercentage]}
                        onValueChange={([value]) => setCustomFormData(prev => ({
                          ...prev,
                          difficultyDistribution: { ...prev.difficultyDistribution, mediumPercentage: value }
                        }))}
                        max={100}
                        step={5}
                        className="w-full"
                        disabled={!customFormData.difficultyDistributionEnabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Hard Questions</span>
                        <span className="text-sm text-muted-foreground">{customFormData.difficultyDistribution.hardPercentage}%</span>
                      </div>
                      <Slider
                        value={[customFormData.difficultyDistribution.hardPercentage]}
                        onValueChange={([value]) => setCustomFormData(prev => ({
                          ...prev,
                          difficultyDistribution: { ...prev.difficultyDistribution, hardPercentage: value }
                        }))}
                        max={100}
                        step={5}
                        className="w-full"
                        disabled={!customFormData.difficultyDistributionEnabled}
                      />
                    </div>

                    <div className={`text-sm font-medium ${!customFormData.difficultyDistributionEnabled || difficultyTotal === 100 ? 'text-green-700' : 'text-amber-700'}`}>
                      {customFormData.difficultyDistributionEnabled
                        ? `Total: ${difficultyTotal}% ${difficultyTotal !== 100 ? '(recommended: 100%)' : ''}`
                        : 'Distribution disabled: system will auto-fill from available questions'}
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>



      {/* Form Actions */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading || externalLoading}>
          {(loading || externalLoading) ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Loading Preview...
            </div>
          ) : (
            'Preview Test'
          )}
        </Button>
      </div>
    </form>
  );
};

function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
